import React, { useEffect, useState } from "react";
import { 
  Typography, 
  CircularProgress, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar
} from "@mui/material";
import { getUserProfile } from "../../../Services/ClintsApi";
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import FeaturesIcon from '@mui/icons-material/Stars';

const MyBouquetsPage = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getUserProfile()
      .then((res) => {
        setUserData(res.data);
      })
      .catch(() => {
        setUserData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <CircularProgress />;
  }

  if (!userData) {
    return <Typography>حدث خطأ أثناء تحميل البيانات.</Typography>;
  }

  const { user, tenant } = userData;

  return (
    <Box sx={{ padding: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          textAlign: "center", 
          fontWeight: "bold", 
          color: "#1976d2",
          mb: 4 
        }}
      >
        ملف العميل والباقة
      </Typography>

      <Grid container spacing={3}>
        {/* User Information Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: "100%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            borderRadius: "16px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white"
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  بيانات العميل
                </Typography>
              </Box>
              
              <Box sx={{ space: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  الاسم: {user.name}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, opacity: 0.9 }}>
                  البريد الإلكتروني: {user.email}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, opacity: 0.9 }}>
                  رقم الهاتف: {user.phone}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, opacity: 0.9 }}>
                  كود العميل: {user.code}
                </Typography>
                <Chip 
                  label={user.active ? "نشط" : "غير نشط"}
                  color={user.active ? "success" : "error"}
                  sx={{ mt: 1 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tenant Information Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: "100%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            borderRadius: "16px",
            background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            color: "white"
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2 }}>
                  <BusinessIcon />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  بيانات الباقة
                </Typography>
              </Box>

              <Box sx={{ space: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  اسم المستأجر: {tenant.id}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, opacity: 0.9 }}>
                  نسخة تجريبية: {tenant.is_demo ? "نعم" : "لا"}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, opacity: 0.9 }}>
                  قاعدة البيانات: {tenant.tenancy_db_name}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, opacity: 0.9 }}>
                  النسخ الاحتياطي: {tenant.database_backup}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  الدومين: {tenant.domains.map((domain) => domain.domain).join(", ")}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Features Table Card */}
        <Grid item xs={12}>
          <Card sx={{ 
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            borderRadius: "16px"
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar sx={{ bgcolor: "#1976d2", mr: 2 }}>
                  <FeaturesIcon />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                  الميزات المفعلة
                </Typography>
              </Box>

              {tenant.features.length > 0 ? (
                <TableContainer component={Paper} sx={{ borderRadius: "12px" }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>اسم الميزة</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>القيمة</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>التكلفة الشهرية</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>التكلفة السنوية</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>تاريخ البداية</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>تاريخ النهاية</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tenant.features.map((feature) => (
                        <TableRow key={feature.id} sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}>
                          <TableCell>{feature.name}</TableCell>
                          <TableCell>
                            <Chip 
                              label={feature.pivot.value} 
                              size="small" 
                              color="primary" 
                            />
                          </TableCell>
                          <TableCell sx={{ color: "#2e7d32", fontWeight: "bold" }}>
                            {feature.pivot.monthly_cost} جنيه
                          </TableCell>
                          <TableCell sx={{ color: "#1565c0", fontWeight: "bold" }}>
                            {feature.pivot.yearly_cost} جنيه
                          </TableCell>
                          <TableCell>{feature.pivot.start_date}</TableCell>
                          <TableCell>{feature.pivot.end_date}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ 
                  textAlign: "center", 
                  py: 4,
                  backgroundColor: "#f8f9fa",
                  borderRadius: "12px"
                }}>
                  <Typography variant="h6" color="textSecondary">
                    لا توجد ميزات مفعلة حاليًا
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MyBouquetsPage;
